<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { useJoakimStore } from '@/stores/useJoakimStore'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'

interface LoanInfo {
  loanNumber: string
  initialAmount: string
  mortgage: string
}

const props = defineProps<{ disable?: boolean }>()
const { t } = useI18n()
const joakim = useJoakimStore()
const { umsokn, onLoaded } = useUmsokn()

function ensureArray() {
  if (!umsokn.value) return
  if (!Array.isArray(umsokn.value.loanInformation))
    umsokn.value.loanInformation = [{ loanNumber: '', initialAmount: '', mortgage: '' }]
  else if (umsokn.value.loanInformation.length === 0)
    umsokn.value.loanInformation.push({ loanNumber: '', initialAmount: '', mortgage: '' })
}
ensureArray()

onLoaded(async () => {
  try { await joakim.getSkuldabref() } catch {}

  const sb: any = (joakim as any).skuldabref
  const rows: any[] = Array.isArray(sb?.skuldabref) ? sb.skuldabref : []

  if (rows.length > 0 && isSingleEmptyRow(umsokn.value.loanInformation)) {
    umsokn.value.loanInformation = rows.map((r: any): LoanInfo => ({
      loanNumber: String(r?.innheimtunumer ?? ''),
      initialAmount: formatMoneyDots(String(r?.nafnverd ?? '')),
      mortgage: String(r?.ved?.[0]?.heimilisfang ?? '')
    }))
  } else {
    ensureArray()
  }
})

const rules = computed(() => {
  const loanRules: Record<number, Record<keyof LoanInfo, any>> = {}
  const arr = umsokn.value?.loanInformation ?? []
  arr.forEach((_:number, i:number) => {
    loanRules[i] = {
      loanNumber: { required },
      initialAmount: { required },
      mortgage: { required }
    }
  })
  return { loanInformation: loanRules }
})

const model = computed(() => ({
  loanInformation: umsokn.value?.loanInformation ?? []
}))

const v$ = useVuelidate(rules, model)

function addChild(): void {
  if (!umsokn.value) return
  umsokn.value.loanInformation.push({ loanNumber: '', initialAmount: '', mortgage: '' })
}

function removeChild(index: number): void {
  if (!umsokn.value) return
  const arr = umsokn.value.loanInformation
  arr.splice(index, 1)
  if (arr.length === 0)
    arr.push({ loanNumber: '', initialAmount: '', mortgage: '' })
}

function formatMoneyDots(v: string): string {
  const digits = String(v || '').replace(/\D/g, '')
  if (!digits) return ''
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
}

function isSingleEmptyRow(arr: LoanInfo[]): boolean {
  if (!Array.isArray(arr) || arr.length !== 1) return false
  const r = arr[0]
  return !(r.loanNumber?.trim() || r.initialAmount?.trim() || r.mortgage?.trim())
}

async function validate(): Promise<boolean> {
  const ok = await v$.value.$validate()
  return ok
}

defineExpose({ validate })
</script>

<template>
  <div v-if="umsokn">
    <v-col>
      <div class="forms__header">
        {{ t('lan_greidslufrest_umsokn_step3_header') }}
      </div>

      <transition-group name="list">
        <div
          v-for="(item, index) in umsokn.loanInformation"
          :key="'loanInformation' + index"
          class="list-item"
        >
          <div v-if="index > 0" class="forms__divider__container">
            <div class="forms__divider"></div>
            <button
              v-if="!props.disable"
              type="button"
              class="forms__warning__list__wrapper"
              @click="removeChild(index)"
            >
              <img class="forms__warning__icon" src="@/assets/icons/Close.svg" alt="Remove" />
            </button>
          </div>

          <v-row>
            <v-col md="4" cols="12">
              <v-text-field
                :disabled="props.disable"
                :label="t('lan_greidslufrest_umsokn_step3_lansnumer')"
                variant="outlined"
                v-model="item.loanNumber"
                :error="v$?.value?.loanInformation?.[index]?.loanNumber?.$dirty && v$?.value?.loanInformation?.[index]?.loanNumber?.$error"
                :error-messages="v$?.value?.loanInformation?.[index]?.loanNumber?.$dirty
                  ? v$?.value?.loanInformation?.[index]?.loanNumber?.$errors.map(() => t('validation_required'))
                  : []"
              />
            </v-col>

            <v-col md="4" cols="12">        
            <v-text-field
              :disabled="props.disable"
              :label="t('lan_greidslufrest_umsokn_step3_fjarhaed')"
              variant="outlined"
              v-model="item.initialAmount"
              inputmode="numeric"
              autocomplete="off"
              @update:modelValue="(v: string) => (item.initialAmount = formatMoneyDots(v))"
              :error="v$?.value?.loanInformation?.[index]?.initialAmount?.$dirty && v$?.value?.loanInformation?.[index]?.initialAmount?.$error"
              :error-messages="v$?.value?.loanInformation?.[index]?.initialAmount?.$dirty
                ? v$?.value?.loanInformation?.[index]?.initialAmount?.$errors.map(() => t('validation_required'))
                : []"
            />
            </v-col>

            <v-col md="4" cols="12">
              <v-text-field
                :disabled="props.disable"
                :label="t('lan_greidslufrest_umsokn_step3_vedstadur')"
                variant="outlined"
                v-model="item.mortgage"
                autocomplete="street-address"
                :error="v$?.value?.loanInformation?.[index]?.mortgage?.$dirty && v$?.value?.loanInformation?.[index]?.mortgage?.$error"
                :error-messages="v$?.value?.loanInformation?.[index]?.mortgage?.$dirty
                  ? v$?.value?.loanInformation?.[index]?.mortgage?.$errors.map(() => t('validation_required'))
                  : []"
              />
            </v-col>
          </v-row>
        </div>
      </transition-group>

      <div class="m-t-40" v-if="!props.disable">
        <v-btn
          color="primary"
          variant="outlined"
          prepend-icon="mdi-plus"
          class="mt-4"
          @click="addChild"
        >
          {{ t('lan_greidslufrest_umsokn_step3_add') }}
        </v-btn>
      </div>
    </v-col>
  </div>
</template>
